import { useCallback, useContext } from "react";
import { SnackBarContext } from "components/snack-bar";
import { ensureDesignPdfGQL } from "../useDesignManagement";
import {
  DesignDownloadError,
  downloadDesign,
  getStoredPdfLink,
  type DesignPdfLinks,
  type DownloadableDesign,
  type DownloadDesignResult,
  type PdfLinkPreference,
} from "./downloadDesign";

interface UseDownloadDesignOptions {
  prefer?: PdfLinkPreference;
  features?: string;
  onGenerated?: (designId: string, links: DesignPdfLinks) => void;
}

export const PREPARING_PDF_MESSAGE = "Preparing your PDF. This can take a few seconds.";
export const PDF_FAILED_MESSAGE = "We could not prepare this PDF. Please try again.";

// Wraps downloadDesign with the app snackbar so a missing PDF is generated with feedback instead of failing silently.
export const useDownloadDesign = ({ prefer, features, onGenerated }: UseDownloadDesignOptions = {}) => {
  const runSnackBar = useContext(SnackBarContext);

  return useCallback(
    async (design: DownloadableDesign): Promise<DownloadDesignResult | null> => {
      if (!getStoredPdfLink(design, prefer)) runSnackBar({ msg: PREPARING_PDF_MESSAGE, type: "info" });
      try {
        const result = await downloadDesign(design, { ensureDesignPdf: ensureDesignPdfGQL, prefer, features });
        if (result.generated) onGenerated?.(design.id, result.links);
        return result;
      } catch (error) {
        console.error(`Design download failed for design ${design.id}:`, error);
        // The PDF exists even if delivering it failed, so the next click uses the stored link.
        if (error instanceof DesignDownloadError && error.links) onGenerated?.(design.id, error.links);
        runSnackBar({ msg: PDF_FAILED_MESSAGE, type: "error" });
        return null;
      }
    },
    [runSnackBar, prefer, features, onGenerated]
  );
};
